import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  fetchMappingVersions,
  fetchTemplate,
  fetchUnmappedValues,
  postAutoMap,
  publishMappingDraft,
  RequestError,
  saveMappingDraft,
  searchTemplates,
  type MappingVersionsState,
  type UnmappedValuesResult,
} from '../../api/dataApi';
import { useStore } from '../../store/StoreContext';
import { useChannelTemplates } from '../../lib/useChannelTemplates';
import type { Mapping, Template } from '../../types';
import { AppCard } from '../ui/AppCard';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { TextArea } from '../ui/TextField';
import type { JsonValue, MappingRule, MappingSpec } from '../../lib/mappingEngine';
import { addOutput, flattenPaths, getOutputs, patchRule, removeOutput, setOutputRules, summarizePreview } from '../../lib/mappingSpecEdit';
import { detectSampleFormat, parseSample, previewMapping, samplePlaceholder, SYSTEM_SAMPLE, type SampleFormat } from '../../lib/mappingPreview';
import { classifyMapping, findSplitRule } from '../../lib/mappingClassify';
import { distinctValues } from '../../lib/signalSplit';

const selectCls =
  'w-full rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-2 py-1 text-[12px] outline-none focus:border-blue-500';

// docs/live-mapping-task.md: a row-level Simple view for a mapping that's
// already resolving cleanly (or has exactly one gap), shown by default in
// place of MappingVisualEditor's three panes. Not a new evaluation path -
// classifyMapping/previewMapping are the SAME code the Advanced editor's
// Live preview already runs, so the two views can never disagree. Only the
// one field this codebase's real production bug turned out to hinge on -
// a `name`/`signalRef` rule whose source carries several distinct values -
// gets a row list; everything else (transforms, casts, multiple outputs)
// stays out of scope, reachable via the "more options" control this card's
// caller wires to MappingVisualEditor.
export function LiveMappingCard({
  mapping,
  onOpenAdvanced,
  onChanged,
}: {
  mapping: Mapping;
  onOpenAdvanced: () => void;
  // Fired after every successful reassign/auto-map/publish - this card owns
  // its own spec/state internally (needed for the row list to update
  // immediately), but the parent screen's own copy of `mapping` (e.g.
  // IntegrationDetailScreen's "Raw spec (JSON)" panel, sourced from
  // useStore()'s cached mappings list) has no other way to learn that
  // anything changed. Without this, that panel silently shows a stale
  // pre-edit spec forever on the same page load - confirmed live: a full
  // Auto-map run correctly updated this card's own row list, but the Raw
  // spec panel below it kept showing an older, partial map.
  onChanged?: () => void;
}) {
  const nickname = mapping.nickname;
  const sourceType = mapping.sourceType.trim().toLowerCase();
  const { channels, updateChannel } = useStore();

  const [state, setState] = useState<MappingVersionsState | null>(null);
  const [loadError, setLoadError] = useState('');
  const [spec, setSpec] = useState<MappingSpec>({});
  const [sampleText, setSampleText] = useState(sourceType === 'system' ? SYSTEM_SAMPLE : '');
  const [busy, setBusy] = useState<string | null>(null); // the value being reassigned, or 'publish'
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [openValue, setOpenValue] = useState<string | null>(null);
  const [openFlatField, setOpenFlatField] = useState<string | null>(null);
  // Which raw values actually need a decision comes from real data, not a
  // pasted/seeded sample - see fetchUnmappedValues's own header comment.
  // A hardcoded sample can only ever cover metrics someone thought to list
  // up front, and drifts the moment a collector adds/renames one; this
  // audits Signals the destination channel has actually received.
  const [unmapped, setUnmapped] = useState<UnmappedValuesResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchMappingVersions(nickname)
      .then((s) => {
        if (cancelled) return;
        setState(s);
        setSpec((s.draft ? s.draft.spec : s.activeSpec) as MappingSpec);
        setLoadError('');
        setConflict(false);
      })
      .catch((e) => !cancelled && setLoadError(e instanceof Error ? e.message : 'Unable to load the mapping.'));
    return () => {
      cancelled = true;
    };
  }, [nickname]);

  useEffect(() => {
    let cancelled = false;
    fetchUnmappedValues(nickname)
      .then((r) => {
        if (!cancelled) setUnmapped(r);
      })
      .catch(() => {
        // Silent: the row list just falls back to whatever's already in
        // rule.map (still correct, just incomplete) rather than blocking
        // the whole card on this one auxiliary read.
      });
    return () => {
      cancelled = true;
    };
  }, [nickname]);

  const destChannel = channels.find((c) => c.nickname === (typeof spec.channel === 'string' ? spec.channel : ''));
  const destTemplates = useChannelTemplates(destChannel);
  const [ownedTemplates, setOwnedTemplates] = useState<Template[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const page = await searchTemplates('mine', '');
        const hydrated = (await Promise.all(page.data.map((n) => fetchTemplate(n)))).filter((t): t is Template => t !== null);
        if (!cancelled) setOwnedTemplates(hydrated);
      } catch {
        if (!cancelled) setOwnedTemplates([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const allTemplates = useMemo(() => {
    const byNickname = new Map(ownedTemplates.map((t) => [t.nickname, t]));
    for (const t of destTemplates) byNickname.set(t.nickname, t);
    return [...byNickname.values()];
  }, [ownedTemplates, destTemplates]);
  const channelSignals = useMemo(
    () => allTemplates.flatMap((t) => t.signals.map((sg) => ({ name: sg.name, number: sg.number, emoji: sg.emoji }))),
    [allTemplates],
  );
  const templateForSignalValue = useMemo(() => {
    const out: Record<string, string> = {};
    for (const t of allTemplates) for (const sg of t.signals) {
      if (sg.number) out[sg.number] = t.nickname;
      out[sg.name] = t.nickname;
    }
    return out;
  }, [allTemplates]);
  const attachTemplateForValue = (value: string) => {
    const tplNickname = templateForSignalValue[value];
    if (!tplNickname || !destChannel || (destChannel.templateIds ?? []).includes(tplNickname)) return;
    void updateChannel({ ...destChannel, templateIds: [...(destChannel.templateIds ?? []), tplNickname] });
  };

  const records = useMemo<JsonValue[]>(() => {
    if (sampleText.trim() === '') return [];
    try {
      const fmt: 'auto' | SampleFormat = 'auto';
      const detected = fmt === 'auto' ? detectSampleFormat(sourceType, sampleText, '', typeof spec.format === 'string' ? spec.format : undefined) : fmt;
      return parseSample(sourceType, sampleText, spec.recordPath, detected);
    } catch {
      return [];
    }
  }, [sampleText, sourceType, spec.recordPath, spec.format]);

  const results = useMemo(() => (records.length ? previewMapping(spec, records, nickname || 'sample') : null), [records, spec, nickname]);
  const summary = useMemo(() => (results ? summarizePreview(spec, results) : null), [results, spec]);
  const classification = useMemo(() => classifyMapping(spec, records, results), [spec, records, results]);
  const outputs = useMemo(() => getOutputs(spec), [spec]);
  const dirty = state !== null && JSON.stringify(spec) !== JSON.stringify(state.activeSpec);

  // Rows: the first output's name/signalRef rule, if it has one - same rule
  // shape findSplitRule already picks out for classifyMapping, just without
  // needing any sample records to find it (it only looks at rule shape).
  const rowOutputIndex = outputs.findIndex((rules) => findSplitRule(rules) !== null);
  const rowRuleIndex = rowOutputIndex >= 0 ? (findSplitRule(outputs[rowOutputIndex]) as { rule: MappingRule; index: number }).index : -1;
  const rowRule: MappingRule | undefined = rowRuleIndex >= 0 ? outputs[rowOutputIndex][rowRuleIndex] : undefined;
  const rowKind = rowRule?.to === 'signalRef' ? 'signalRef' : 'name';

  // One row per value already declared in rule.map (so a deliberate choice
  // never disappears just because that Signal hasn't fired again recently),
  // plus every value the live audit found falling through it (real Signals
  // this channel has actually received - see fetchUnmappedValues), plus
  // whatever the pasted/seeded sample shows (the only source available
  // before this mapping has ever sent anything live, or while testing a
  // hypothetical payload that hasn't gone out yet). Real data always wins
  // when both know about a value; the sample only fills in what neither
  // rule.map nor the live audit has seen yet.
  const rowValues = useMemo(() => {
    if (!rowRule) return [] as string[];
    const mappedKeys = Object.keys(rowRule.map ?? {}).sort();
    const seen = new Set(mappedKeys);
    const liveUnmapped = unmapped?.auditable
      ? [...unmapped.unmapped]
          .sort((a, b) => b.count - a.count)
          .map((u) => u.value)
          .filter((v) => !seen.has(v))
      : [];
    for (const v of liveUnmapped) seen.add(v);
    const sampleValues = rowRule.from ? distinctValues(records, rowRule.from).filter((v) => !seen.has(v)) : [];
    return [...mappedKeys, ...liveUnmapped, ...sampleValues];
  }, [rowRule, unmapped, records]);

  const pendingValues = useMemo(
    () => (rowRule ? rowValues.filter((v) => !rowRule.map || rowRule.map[v] === undefined) : []),
    [rowRule, rowValues],
  );

  // Flat-field mapping (host-metrics style): a payload like
  // {"bathroom": 15250, "outdoor": 7500} has no single discriminator field
  // (findSplitRule finds nothing), so the rowRule/rowValues machinery above
  // never applies - each key is its own Signal, not a value of one field.
  // Already-mapped fields are read straight off the saved spec - the target
  // Output's `from` and its sibling name/signalRef rule's `const` - so they
  // show up the instant this card loads, with no sample pasted at all
  // (exactly like the rowRule list above needs no sample once rule.map has
  // entries). A pasted/seeded sample only ever adds NEW, still-unmapped
  // fields to the list; it's never required to see what's already decided.
  const outputIndexForPath = (path: string) => outputs.findIndex((rules) => rules.some((r) => r.from === path));
  const mappedFlatFields = useMemo(() => {
    if (rowRule) return [];
    const rows: { path: string; sample: string; mappedTo: string }[] = [];
    for (const rules of outputs) {
      const fromRule = rules.find((r) => typeof r.from === 'string');
      const idRule = rules.find((r) => r.to === 'signalRef' || r.to === 'name');
      if (fromRule?.from && idRule?.const !== undefined) rows.push({ path: fromRule.from, sample: '', mappedTo: String(idRule.const) });
    }
    return rows;
  }, [rowRule, outputs]);
  const flatFields = useMemo(() => {
    if (rowRule) return [];
    const mappedPaths = new Set(mappedFlatFields.map((f) => f.path));
    const newFields = flattenPaths(records[0])
      .filter((p) => !mappedPaths.has(p.path))
      .map((p) => ({ ...p, mappedTo: null as string | null }));
    return [...mappedFlatFields.map((f) => ({ ...f, mappedTo: f.mappedTo as string | null })), ...newFields];
  }, [rowRule, records, mappedFlatFields]);
  const guessSignalFor = (path: string): string => {
    const leaf = path.split('.').pop() ?? path;
    const sg = channelSignals.find((s) => s.name.toLowerCase() === leaf.toLowerCase());
    return sg ? sg.number ?? sg.name : '';
  };
  const flatTargetLabel = (target: string): string => {
    const sg = channelSignals.find((s) => s.number === target || s.name === target);
    return sg ? `${sg.emoji} ${sg.name}${sg.number ? ` #${sg.number}` : ''}` : target;
  };

  const reassign = async (value: string, target: string) => {
    if (!state || !rowRule || rowRuleIndex < 0) return;
    setBusy(value);
    setError('');
    try {
      const nextMap = { ...(rowRule.map ?? {}) };
      if (target) nextMap[value] = target;
      else delete nextMap[value];
      const nextRule = patchRule(rowRule, { map: Object.keys(nextMap).length ? nextMap : undefined });
      const nextSpec = setOutputRules(spec, rowOutputIndex, outputs[rowOutputIndex].map((r, i) => (i === rowRuleIndex ? nextRule : r)));
      await saveMappingDraft(nickname, nextSpec as Record<string, unknown>, state.activeVersion);
      const published = await publishMappingDraft(nickname, state.activeVersion);
      if (target) attachTemplateForValue(target);
      // The backend now reflects nextSpec at published.version - trust that
      // rather than round-tripping a GET, so the row updates immediately
      // even if a slower read-your-write path lags behind the write.
      setState({ ...state, activeVersion: published.version, activeSpec: nextSpec as Record<string, unknown>, draft: null });
      setSpec(nextSpec);
      setOpenValue(null);
      // This value is now explicitly decided either way - drop it from the
      // live-audit list immediately rather than waiting for a re-fetch
      // (the audit itself won't reflect this until new Signals stream in
      // under the republished mapping).
      setUnmapped((u) => (u?.unmapped ? { ...u, unmapped: u.unmapped.filter((row) => row.value !== value) } : u));
      onChanged?.();
    } catch (e) {
      if (e instanceof RequestError && e.status === 409) setConflict(true);
      else setError(e instanceof Error ? e.message : 'Unable to publish that change.');
    } finally {
      setBusy(null);
    }
  };

  // Writes one field's target into `spec`: patches the existing Output in
  // place when the field is already mapped (so reassigning never leaves a
  // duplicate around), otherwise fills the mapping's still-empty default
  // output for the first field rather than leaving it dangling, then
  // appends a new output per field after that - same shape
  // MappingVisualEditor's builder produces by hand. Pure (takes outputs
  // rather than reading them off state) so autoMapFlatFields below can fold
  // several fields into one spec before ever calling setSpec - reading
  // component state between fields would only ever see the pre-loop value,
  // since none of these awaited calls yield a re-render mid-loop.
  const withFlatFieldOutput = (base: MappingSpec, baseOutputs: MappingRule[][], path: string, target: string): MappingSpec => {
    const existingIdx = baseOutputs.findIndex((rules) => rules.some((r) => r.from === path));
    if (existingIdx >= 0) {
      const rules = baseOutputs[existingIdx];
      const idRuleIdx = rules.findIndex((r) => r.to === 'signalRef' || r.to === 'name');
      const nextRules = idRuleIdx >= 0 ? rules.map((r, i) => (i === idRuleIdx ? patchRule(r, { const: target }) : r)) : [...rules, { to: 'signalRef', const: target }];
      return setOutputRules(base, existingIdx, nextRules);
    }
    const newRules: MappingRule[] = [
      { to: 'signalRef', const: target },
      { to: 'value', from: path },
    ];
    return baseOutputs.length === 1 && baseOutputs[0].length === 0
      ? setOutputRules(base, 0, newRules)
      : setOutputRules(addOutput(base), baseOutputs.length, newRules);
  };

  // Unmaps a field: clears its Output's rules (rather than leaving a
  // signalRef rule with no value behind) - removeOutput only drops the
  // output entirely when more than one exists, matching MappingVisualEditor.
  const withoutFlatFieldOutput = (base: MappingSpec, baseOutputs: MappingRule[][], path: string): MappingSpec => {
    const idx = baseOutputs.findIndex((rules) => rules.some((r) => r.from === path));
    if (idx < 0) return base;
    return baseOutputs.length > 1 ? removeOutput(base, idx) : setOutputRules(base, 0, []);
  };

  // Flat-field counterpart to reassign(): one field, one Output - patched in
  // place if the field was already mapped, otherwise a new one.
  const mapFlatField = async (path: string, target: string) => {
    if (!state) return;
    const wasMapped = outputIndexForPath(path) >= 0;
    if (!target && !wasMapped) return; // "Skip" on a still-unmapped field - nothing to do
    setBusy(path);
    setError('');
    try {
      const nextSpec = target ? withFlatFieldOutput(spec, outputs, path, target) : withoutFlatFieldOutput(spec, outputs, path);
      await saveMappingDraft(nickname, nextSpec as Record<string, unknown>, state.activeVersion);
      const published = await publishMappingDraft(nickname, state.activeVersion);
      if (target) attachTemplateForValue(target);
      setState({ ...state, activeVersion: published.version, activeSpec: nextSpec as Record<string, unknown>, draft: null });
      setSpec(nextSpec);
      setOpenFlatField(null);
      onChanged?.();
    } catch (e) {
      if (e instanceof RequestError && e.status === 409) setConflict(true);
      else setError(e instanceof Error ? e.message : 'Unable to publish that change.');
    } finally {
      setBusy(null);
    }
  };

  // Bulk version: every still-unmapped field whose name exactly matches an
  // existing Signal (case-insensitive), folded into one spec and published
  // once - there's no batched backend endpoint for this shape (auto-map/
  // postAutoMap is the signalRef/name value-map audit, a different rule
  // shape entirely). Fields with no name match are left for the per-row
  // dropdown below.
  const flatAutoMatches = useMemo(
    () => flatFields.filter((f) => f.mappedTo === null && guessSignalFor(f.path) !== ''),
    [flatFields, channelSignals],
  );
  const autoMapFlatFields = async () => {
    if (!state || flatAutoMatches.length === 0) return;
    setBusy('flat-automap');
    setError('');
    try {
      let nextSpec = spec;
      for (const f of flatAutoMatches) nextSpec = withFlatFieldOutput(nextSpec, getOutputs(nextSpec), f.path, guessSignalFor(f.path));
      await saveMappingDraft(nickname, nextSpec as Record<string, unknown>, state.activeVersion);
      const published = await publishMappingDraft(nickname, state.activeVersion);
      for (const f of flatAutoMatches) attachTemplateForValue(guessSignalFor(f.path));
      setState({ ...state, activeVersion: published.version, activeSpec: nextSpec as Record<string, unknown>, draft: null });
      setSpec(nextSpec);
      onChanged?.();
    } catch (e) {
      if (e instanceof RequestError && e.status === 409) setConflict(true);
      else setError(e instanceof Error ? e.message : 'Unable to auto-map.');
    } finally {
      setBusy(null);
    }
  };

  // Bulk version of reassign, server-authoritative: POST /mappings/
  // {nickname}/auto-map does the exact-name-match + map-update +
  // save+publish itself now (backend/modules/mapping/transitions/
  // automap.go's AutoMap) - this used to duplicate that matching logic
  // client-side, which meant the CLI/zmac had no way to get the same
  // result without reimplementing it a third time. One network call for
  // the whole batch either way; the difference is just who computes the
  // matches. Refetches afterwards since the endpoint only reports a count,
  // not which value went where - attachTemplateForValue still runs here
  // for every newly-added entry the refetch reveals, so a matched target's
  // template still gets attached to the destination channel exactly as
  // before.
  const autoMapAll = async () => {
    if (!state || !rowRule || rowRuleIndex < 0 || pendingValues.length === 0) return;
    setBusy('automap');
    setError('');
    try {
      const previousMap = { ...(rowRule.map ?? {}) };
      await postAutoMap(nickname);
      const refreshed = await fetchMappingVersions(nickname);
      const nextSpec = (refreshed.draft ? refreshed.draft.spec : refreshed.activeSpec) as MappingSpec;
      const nextRule = getOutputs(nextSpec)[rowOutputIndex]?.[rowRuleIndex];
      for (const [value, target] of Object.entries(nextRule?.map ?? {})) {
        if (previousMap[value] === undefined && typeof target === 'string') attachTemplateForValue(target);
      }
      setState(refreshed);
      setSpec(nextSpec);
      try {
        setUnmapped(await fetchUnmappedValues(nickname));
      } catch {
        // Best-effort refresh only - the map itself already reflects the
        // real published result regardless of whether this re-audit lands.
      }
      onChanged?.();
    } catch (e) {
      if (e instanceof RequestError && e.status === 409) setConflict(true);
      else setError(e instanceof Error ? e.message : 'Unable to auto-map.');
    } finally {
      setBusy(null);
    }
  };

  const publish = async () => {
    if (!state) return;
    setBusy('publish');
    setError('');
    try {
      await saveMappingDraft(nickname, spec as Record<string, unknown>, state.activeVersion);
      const published = await publishMappingDraft(nickname, state.activeVersion);
      setState({ ...state, activeVersion: published.version, activeSpec: spec as Record<string, unknown>, draft: null });
      onChanged?.();
    } catch (e) {
      if (e instanceof RequestError && e.status === 409) setConflict(true);
      else setError(e instanceof Error ? e.message : 'Unable to publish.');
    } finally {
      setBusy(null);
    }
  };

  const reload = () => {
    setConflict(false);
    fetchMappingVersions(nickname).then((s) => {
      setState(s);
      setSpec((s.draft ? s.draft.spec : s.activeSpec) as MappingSpec);
    });
  };

  if (!state) {
    return loadError ? (
      <p className="text-sm text-red-500 m-0">{loadError}</p>
    ) : (
      <div className="py-3 text-center text-sm text-black/40 dark:text-white/40">
        <Loader2 size={14} className="inline animate-spin" /> Loading…
      </div>
    );
  }

  const targetLabel = (value: string): string => {
    const mapped = rowRule?.map?.[value];
    if (mapped) {
      const sg = channelSignals.find((s) => s.number === mapped || s.name === mapped);
      return sg?.number ? `Signal #${sg.number}` : String(mapped);
    }
    const matched = channelSignals.some((s) => s.name === value);
    return matched ? `${value} (matched by name)` : `${value} (new Signal)`;
  };

  // The row list (rowValues) needs no sample at all - it's driven by the
  // live unmapped-values audit plus rule.map's own already-declared keys.
  // A sample only matters here for the illustrative produced/rejected
  // count and the required-field warning below; with nothing pasted yet
  // and no rows to show either, there's genuinely nothing this card can
  // say, so it stays a plain paste target exactly as before.
  if (records.length === 0 && rowValues.length === 0 && mappedFlatFields.length === 0) {
    return (
      <AppCard className="flex flex-col gap-2">
        <span className="text-sm font-semibold">{mapping.title || nickname}</span>
        <p className="text-xs text-black/45 dark:text-white/45 m-0">Paste a sample to begin - nothing is sent anywhere.</p>
        <TextArea
          aria-label="Sample payload"
          value={sampleText}
          onChange={(e) => setSampleText(e.target.value)}
          placeholder={samplePlaceholder(sourceType, typeof spec.format === 'string' ? spec.format : undefined)}
          className="font-mono text-[12px] min-h-[100px]"
          spellCheck={false}
        />
        <Button variant="bordered" onClick={onOpenAdvanced}>
          More options
        </Button>
      </AppCard>
    );
  }

  return (
    <AppCard className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold flex-1">{mapping.title || nickname}</span>
        <button aria-label="More options" title="More options" onClick={onOpenAdvanced} className="rounded-lg border border-black/15 dark:border-white/15 p-1.5 text-black/40 dark:text-white/40 hover:text-black/70 dark:hover:text-white/70">
          <span className="inline-flex gap-0.5">
            <span className="h-1 w-1 rounded-full bg-current" />
            <span className="h-1 w-1 rounded-full bg-current" />
            <span className="h-1 w-1 rounded-full bg-current" />
          </span>
        </button>
      </div>

      {conflict && (
        <div role="alert" className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm flex items-center gap-2 flex-wrap">
          <span>This integration changed since you loaded it.</span>
          <Button variant="bordered" onClick={reload}>
            Reload
          </Button>
        </div>
      )}
      {error && <p role="alert" className="text-sm text-red-500 m-0">{error}</p>}

      {summary && (
        <Badge>
          {summary.produced} / {summary.records} resolving, {summary.rejected} rejected
        </Badge>
      )}

      {classification.state === 'needs-decision' && classification.gap.kind === 'required-empty' && (
        <p className="text-sm text-red-500 m-0">
          <code className="font-mono">{classification.gap.field}</code> is required but resolves to nothing on some records - those Signals are
          silently dropped.
        </p>
      )}

      {rowRule && rowValues.length > 0 && (
        <div className="flex flex-col gap-1">
          {rowValues.map((v) => (
            <div key={v} className="flex flex-col">
              <button
                type="button"
                onClick={() => setOpenValue((cur) => (cur === v ? null : v))}
                className="flex items-center gap-2 rounded-lg px-1 py-1.5 text-left text-[13px] hover:bg-black/[0.03] dark:hover:bg-white/[0.05]"
              >
                <span className="font-mono font-semibold flex-none w-40 truncate" title={v}>
                  {v}
                </span>
                <span className="text-black/40 dark:text-white/40">→</span>
                <span className="flex-1 truncate text-black/60 dark:text-white/60">{targetLabel(v)}</span>
              </button>
              {openValue === v && (
                <div className="ml-2 mb-1 flex flex-col gap-0.5 rounded-lg border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.04] p-1">
                  <select
                    aria-label={`Signal for ${v}`}
                    value={rowRule.map?.[v] ? String(rowRule.map[v]) : ''}
                    disabled={busy === v}
                    onChange={(e) => void reassign(v, e.target.value)}
                    className={selectCls}
                  >
                    <option value="">Create a new Signal</option>
                    {channelSignals.map((sg, idx) => {
                      const val = rowKind === 'signalRef' ? sg.number : sg.name;
                      return (
                        <option key={`${sg.name}-${idx}`} value={val ?? ''} disabled={!val}>
                          {sg.emoji} {sg.name} {sg.number ? `#${sg.number}` : rowKind === 'signalRef' ? '(no number yet)' : ''}
                        </option>
                      );
                    })}
                  </select>
                  {busy === v && <span className="text-[11px] text-black/40 dark:text-white/40">Publishing…</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {flatFields.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-black/40 dark:text-white/40 flex-1">Fields in this sample</span>
            {flatAutoMatches.length > 0 && (
              <Button variant="bordered" onClick={() => void autoMapFlatFields()} disabled={busy !== null}>
                Auto-map {flatAutoMatches.length} matching
              </Button>
            )}
          </div>
          {flatFields.map((f) => {
            const guessed = guessSignalFor(f.path);
            const isOpen = openFlatField === f.path;
            // Already mapped: a plain resolved row (path -> Signal), same
            // pattern as the rowRule list above - click to reassign or clear
            // it. Not yet mapped: the dropdown is right there, no extra tap.
            if (f.mappedTo !== null && !isOpen) {
              return (
                <button
                  key={f.path}
                  type="button"
                  onClick={() => setOpenFlatField(f.path)}
                  className="flex items-center gap-2 rounded-lg px-1 py-1.5 text-left text-[13px] hover:bg-black/[0.03] dark:hover:bg-white/[0.05]"
                >
                  <span className="font-mono font-semibold flex-none w-28 truncate" title={f.path}>
                    {f.path}
                  </span>
                  <span className="text-black/40 dark:text-white/40">→</span>
                  <span className="flex-1 truncate text-black/60 dark:text-white/60">{flatTargetLabel(f.mappedTo)}</span>
                </button>
              );
            }
            return (
              <div key={f.path} className="flex items-center gap-2">
                <span className="font-mono font-semibold flex-none w-28 truncate" title={f.path}>
                  {f.path}
                </span>
                <span className="flex-none w-16 truncate text-[12px] text-black/40 dark:text-white/40" title={f.sample}>
                  {f.sample}
                </span>
                <span className="text-black/40 dark:text-white/40">→</span>
                <select
                  aria-label={`Signal for ${f.path}`}
                  value={f.mappedTo ?? ''}
                  disabled={busy === f.path}
                  onChange={(e) => void mapFlatField(f.path, e.target.value)}
                  className={selectCls}
                >
                  <option value="">
                    {f.mappedTo !== null ? 'Remove mapping' : guessed ? `Skip (matches ${guessed})` : 'Skip this field'}
                  </option>
                  {channelSignals.map((sg, idx) => (
                    <option key={`${sg.name}-${idx}`} value={sg.number ?? sg.name} disabled={!sg.number}>
                      {sg.emoji} {sg.name} {sg.number ? `#${sg.number}` : '(no number yet)'}
                    </option>
                  ))}
                </select>
                {busy === f.path && <span className="text-[11px] text-black/40 dark:text-white/40">Publishing…</span>}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {pendingValues.length > 0 && (
          <Button variant="bordered" onClick={() => void autoMapAll()} disabled={busy !== null}>
            {busy === 'automap' ? 'Auto-mapping…' : `Auto-map ${pendingValues.length} remaining`}
          </Button>
        )}
        <Button onClick={() => void publish()} disabled={!dirty || busy !== null}>
          {busy === 'publish' ? 'Publishing…' : 'Publish mapping'}
        </Button>
        {!dirty && <span className="text-xs text-black/45 dark:text-white/45">Up to date - nothing to publish.</span>}
      </div>
    </AppCard>
  );
}
